import { Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatPrefix, MatSuffix } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FortressSearchResult, FortressSearchService } from '@fortress/core';
import { debounceTime, filter } from 'rxjs';
import { RoutePaths } from '../route-paths.model';
import { FortressTypeLabelComponent } from '../shared/fortress-type-label/fortress-type-label.component';
import { SearchErrorDialogComponent } from './search-error-dialog/search-error-dialog.component';

@Component({
  selector: 'app-fortress-search',
  imports: [
    MatInput,
    MatFormField,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    FortressTypeLabelComponent,
    MatIcon,
    MatPrefix,
    MatIconButton,
    MatTooltip,
    MatSuffix,
    MatProgressSpinner,
  ],
  templateUrl: './fortress-search.component.html',
  styleUrl: './fortress-search.component.scss',
})
export class FortressSearchComponent {
  searchInput = new FormControl<string | FortressSearchResult>('', { nonNullable: true });
  readonly searchTerm = toSignal(
    this.searchInput.valueChanges.pipe(
      // Only allow searching when the input value is a string. Selecting an autocomplete option updates the control value to a FortressSearchResult object, which should not execute a new search
      filter((val): val is string => typeof val === 'string'),
      // Filter out empty values and if an empty value exists, clear the results resource
      filter((val) => {
        const hasInput = val.length > 0;
        if (!hasInput) {
          this.searchResultsResource.set(undefined);
        }
        return hasInput;
      }),
      debounceTime(500),
    ),
  );
  searchResultsResource = inject(FortressSearchService).searchResource(this.searchTerm);
  searchResultsTop5 = computed(
    // Show max of 5 results at all times, up to 4 when error exists as the error display takes a mat-option slot
    () => this.searchResultsResource.value()?.slice(0, this.searchErrors().length > 0 ? 4 : 5) ?? [],
  );
  searchErrors = inject(FortressSearchService).searchErrors;
  private dialogService = inject(MatDialog);
  private router = inject(Router);

  viewErrorDetails = () =>
    this.dialogService.open<SearchErrorDialogComponent, Signal<string[]>>(SearchErrorDialogComponent, {
      data: this.searchErrors,
    });

  reset = () => {
    this.searchInput.reset();
    this.searchResultsResource.set(undefined);
  };

  selectedResultDisplayFn = (result: string | FortressSearchResult) =>
    typeof result === 'string' ? result : result.value;

  navigateToResult = (result: MatAutocompleteSelectedEvent) => {
    const routePath = this.getResultRoutePath(result.option.value as FortressSearchResult);
    void this.router.navigate(routePath);
  };

  private getResultRoutePath = (result: FortressSearchResult) => {
    switch (result.type) {
      case 'ROLE':
        return [`/${RoutePaths.Base}`, RoutePaths.Roles, result.value];
      case 'PERMISSION_OBJECT':
        return [`/${RoutePaths.Base}`, RoutePaths.Permissions, result.value];
      // userId path params need to end with _access. Alternatively, we could consider automatically applying _access to the path param value when loading the user details routed component.
      case 'USER':
        return [`/${RoutePaths.Base}`, RoutePaths.Users, `${result.value}_access`];
      default:
        return [];
    }
  };
}
